import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Collapse,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/formatters';

const TopThree = ({ agents, profilePictures }) => {
  // Display order: 2nd left, 1st center, 3rd right
  const displayOrder = [1, 0, 2];
  const sizes = { 0: 72, 1: 56, 2: 56 };
  const borderColors = {
    0: '#2C9C6C',
    1: '#2C9C6C',
    2: '#8A9B93',
  };
  const markerColors = {
    0: '#2C9C6C',
    1: '#2C9C6C',
    2: '#8A9B93',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: 1.5,
        py: 1.5,
      }}
    >
      {displayOrder.map((origIdx) => {
        const agent = agents[origIdx];
        if (!agent) return null;
        const size = sizes[origIdx];
        const borderColor = borderColors[origIdx];
        const markerColor = markerColors[origIdx];
        const pic = profilePictures[agent.name];

        return (
          <Box
            key={agent.name}
            sx={{
              flex: 1,
              width: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              order: origIdx === 0 ? 2 : origIdx === 1 ? 1 : 3,
              mb: origIdx === 0 ? 1 : 0,
            }}
          >
            {/* Rank number */}
            <Typography sx={{
              fontSize: origIdx === 0 ? '0.85rem' : '0.75rem',
              fontWeight: 700,
              color: markerColor,
              mb: 0.3,
            }}>
              {origIdx + 1}
            </Typography>

            {/* Crown for 1st, triangle for 2nd/3rd */}
            {origIdx === 0 ? (
              <Typography sx={{ fontSize: '1.1rem', mb: 0.3, lineHeight: 1 }}>
                👑
              </Typography>
            ) : (
              <Typography sx={{
                fontSize: '0.65rem',
                color: markerColor,
                mb: 0.3,
                lineHeight: 1,
              }}>
                ▲
              </Typography>
            )}

            {/* Avatar with ring */}
            <Box sx={{ position: 'relative', mb: 0.8 }}>
              <Avatar
                src={pic || undefined}
                sx={{
                  width: size,
                  height: size,
                  border: `3px solid ${borderColor}`,
                  bgcolor: '#E8EDEB',
                  color: borderColor,
                }}
              >
                {!pic && <PersonIcon sx={{ fontSize: size * 0.5 }} />}
              </Avatar>
            </Box>

            {/* Name */}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: origIdx === 0 ? '0.8rem' : '0.7rem',
                color: 'text.primary',
                textAlign: 'center',
                width: '100%',
                lineHeight: 1.2,
              }}
            >
              {agent.name}
            </Typography>

            {/* Score */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: origIdx === 0 ? '1rem' : '0.85rem',
                color: '#2C9C6C',
                mt: 0.3,
              }}
            >
              {formatCurrency(agent.totalAP)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const AgentRow = ({ agent, rank, profilePictures }) => {
  const pic = profilePictures[agent.name];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.secondary', minWidth: 24 }}>
        #{rank}
      </Typography>
      <Avatar
        src={pic || undefined}
        sx={{ width: 32, height: 32, bgcolor: '#111C17' }}
      >
        {!pic && <PersonIcon sx={{ fontSize: 18 }} />}
      </Avatar>
      <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }} noWrap>
        {agent.name}
      </Typography>
      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'primary.main' }}>
        {formatCurrency(agent.totalAP)}
      </Typography>
    </Box>
  );
};

const MobileLeaderboard = ({ topAgents, profilePictures, t }) => {
  const [expanded, setExpanded] = useState(false);

  const top3 = topAgents.slice(0, 3);
  const fourthFifth = topAgents.slice(3, 5);
  const rest = topAgents.slice(5);

  return (
    <Paper sx={{ p: 1.5, mb: 1.5 }}>
      {top3.length > 0 ? (
        <>
          <TopThree agents={top3} profilePictures={profilePictures} />

          {fourthFifth.map((agent, i) => (
            <AgentRow
              key={agent.name}
              agent={agent}
              rank={i + 4}
              profilePictures={profilePictures}
            />
          ))}

          {rest.length > 0 && (
            <>
              <Collapse in={expanded}>
                {rest.map((agent, i) => (
                  <AgentRow
                    key={agent.name}
                    agent={agent}
                    rank={i + 6}
                    profilePictures={profilePictures}
                  />
                ))}
              </Collapse>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
                sx={{ mt: 1, width: '100%', color: 'text.secondary' }}
              >
                {expanded ? t('dashboard.showLess') : t('dashboard.showAll')}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography sx={{ textAlign: 'center', py: 3, color: 'text.secondary', fontSize: '0.9rem' }}>
          {t('dashboard.noDataForFilters')}
        </Typography>
      )}
    </Paper>
  );
};

export default MobileLeaderboard;
